import { useMemo } from 'react';
import { JournalEntry } from '../../../types';

const SETUP_TYPES = [
  'VWAP Reclaim Long', 'VWAP Rejection Short', 'OR Breakout Long', 'OR Failed Breakout Short',
  'Liquidity Sweep Reversal', 'Gamma Acceleration', 'Extension Fade', 'Trend Pullback', 'Max Pain Pin', 'Custom',
];

export interface JournalStats {
  completed: JournalEntry[];
  wins: JournalEntry[];
  losses: JournalEntry[];
  winRate: string | null;
  avgWin: string | null;
  avgLoss: string | null;
  totalPL: number;
  avgR: string | null;
  expectancy: string | null;
  streak: { count: number; type: 'win' | 'loss' | 'none' };
  byTag: { tag: string; count: number; winRate: string; avgPL: string }[];
  bySymbol: { symbol: string; count: number; winRate: string; totalPL: string; avgR: string }[];
  bySetup: { setup: string; count: number; winRate: string; avgPL: string; avgR: string }[];
  byRegime: { regime: string; count: number; winRate: string; avgPL: string }[];
}

export function useJournalState(journal: JournalEntry[]): JournalStats {
  const completed = useMemo(() => journal.filter(e => e.result !== null && e.outcome !== 'open'), [journal]);
  const wins = useMemo(() => completed.filter(e => e.outcome === 'win'), [completed]);
  const losses = useMemo(() => completed.filter(e => e.outcome === 'loss'), [completed]);

  const winRate = completed.length ? (wins.length / completed.length * 100).toFixed(0) : null;
  const avgWin = wins.length ? (wins.reduce((s, e) => s + (e.result || 0), 0) / wins.length).toFixed(2) : null;
  const avgLoss = losses.length ? (losses.reduce((s, e) => s + (e.result || 0), 0) / losses.length).toFixed(2) : null;
  const totalPL = completed.reduce((s, e) => s + (e.result || 0), 0);
  const avgR = completed.filter(e => e.rMultiple !== null).length
    ? (completed.reduce((s, e) => s + (e.rMultiple || 0), 0) / completed.filter(e => e.rMultiple !== null).length).toFixed(2)
    : null;

  const expectancy = useMemo(() => {
    if (!completed.length || !wins.length || !losses.length) return null;
    const wr = wins.length / completed.length;
    const lr = losses.length / completed.length;
    const avgW = wins.reduce((s, e) => s + (e.result || 0), 0) / wins.length;
    const avgL = losses.reduce((s, e) => s + (e.result || 0), 0) / losses.length;
    return ((wr * avgW) + (lr * avgL)).toFixed(2);
  }, [completed, wins, losses]);

  const streak = useMemo(() => {
    const sorted = [...completed].sort((a, b) => b.id - a.id);
    if (!sorted.length) return { count: 0, type: 'none' as const };
    let count = 0;
    const type = sorted[0].outcome === 'win' ? 'win' : 'loss';
    for (const e of sorted) {
      if (e.outcome === type) count++;
      else break;
    }
    return { count, type: type as 'win' | 'loss' };
  }, [completed]);

  const byTag = useMemo(() => {
    const map: Record<string, { count: number; wins: number; totalPL: number }> = {};
    completed.forEach(e => {
      (e.tags || []).forEach(tag => {
        if (!map[tag]) map[tag] = { count: 0, wins: 0, totalPL: 0 };
        map[tag].count++;
        if (e.outcome === 'win') map[tag].wins++;
        map[tag].totalPL += e.result || 0;
      });
    });
    return Object.entries(map)
      .map(([tag, v]) => ({ tag, count: v.count, winRate: (v.wins/v.count*100).toFixed(0), avgPL: (v.totalPL/v.count).toFixed(2) }))
      .sort((a, b) => b.count - a.count);
  }, [completed]);

  const bySymbol = useMemo(() => {
    const map: Record<string, { count: number; wins: number; totalPL: number; rValues: number[] }> = {};
    completed.forEach(e => {
      if (!e.symbol) return;
      if (!map[e.symbol]) map[e.symbol] = { count: 0, wins: 0, totalPL: 0, rValues: [] };
      map[e.symbol].count++;
      if (e.outcome === 'win') map[e.symbol].wins++;
      map[e.symbol].totalPL += e.result || 0;
      if (e.rMultiple !== null) map[e.symbol].rValues.push(e.rMultiple);
    });
    return Object.entries(map)
      .map(([sym, v]) => ({
        symbol: sym, count: v.count,
        winRate: (v.wins/v.count*100).toFixed(0),
        totalPL: v.totalPL.toFixed(2),
        avgR: v.rValues.length ? (v.rValues.reduce((s, r) => s+r, 0)/v.rValues.length).toFixed(2) : '—',
      }))
      .sort((a, b) => parseFloat(b.totalPL) - parseFloat(a.totalPL));
  }, [completed]);

  const bySetup = useMemo(() => {
    const map: Record<string, { count: number; wins: number; totalPL: number; avgR: number[] }> = {};
    SETUP_TYPES.forEach(s => { map[s] = { count: 0, wins: 0, totalPL: 0, avgR: [] }; });
    completed.forEach(e => {
      if (!e.setup) return;
      if (!map[e.setup]) map[e.setup] = { count: 0, wins: 0, totalPL: 0, avgR: [] };
      map[e.setup].count++;
      if (e.outcome === 'win') map[e.setup].wins++;
      map[e.setup].totalPL += e.result || 0;
      if (e.rMultiple !== null) map[e.setup].avgR.push(e.rMultiple);
    });
    return Object.entries(map).filter(([, v]) => v.count > 0).map(([setup, v]) => ({
      setup, count: v.count,
      winRate: (v.wins / v.count * 100).toFixed(0),
      avgPL: (v.totalPL / v.count).toFixed(2),
      avgR: v.avgR.length ? (v.avgR.reduce((s, r) => s + r, 0) / v.avgR.length).toFixed(2) : '—',
    })).sort((a, b) => b.count - a.count);
  }, [completed]);

  const byRegime = useMemo(() => {
    const map: Record<string, { count: number; wins: number; totalPL: number }> = {};
    completed.forEach(e => {
      const r = e.regime || 'Unknown';
      if (!map[r]) map[r] = { count: 0, wins: 0, totalPL: 0 };
      map[r].count++;
      if (e.outcome === 'win') map[r].wins++;
      map[r].totalPL += e.result || 0;
    });
    return Object.entries(map).map(([regime, v]) => ({
      regime, count: v.count,
      winRate: (v.wins / v.count * 100).toFixed(0),
      avgPL: (v.totalPL / v.count).toFixed(2),
    })).sort((a, b) => parseFloat(b.winRate) - parseFloat(a.winRate));
  }, [completed]);

  return { completed, wins, losses, winRate, avgWin, avgLoss, totalPL, avgR, expectancy, streak, byTag, bySymbol, bySetup, byRegime };
}

export function buildExportCSV(completed: JournalEntry[]): string {
  const headers = ['Date','Symbol','Setup','Regime','Dir','Entry','Stop','Target','Exit','Result','R-Multiple','Outcome','Tags','Notes'];
  const rows = completed.map(e => [
    e.date, e.symbol, e.setup, e.regime, e.dir,
    e.entry ?? '', e.stop ?? '', e.target ?? '', e.exit ?? '',
    e.result ?? '', e.rMultiple ?? '', e.outcome ?? '',
    (e.tags || []).join(';'),
    `"${(e.notes || '').replace(/"/g,'""')}"`,
  ]);
  return [headers, ...rows].map(r => r.join(',')).join('\n');
}
